import React, { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Plus, UserCog, Trash2, Pencil, Mail, Phone, AlertTriangle } from "lucide-react";
import { toast } from "sonner";

const ROLES = [
  { v: "makeup", label: "Makeup", icon: "💄" },
  { v: "hair", label: "Hair", icon: "✂️" },
  { v: "both", label: "Entrambi", icon: "✨" },
  { v: "assistente", label: "Assistente", icon: "🤝" },
];

const ROLE_LABEL_LEGACY = {
  makeup_artist: "Makeup",
  hairstylist: "Hair",
  assistant: "Assistente",
};

const COLOR_PALETTE = [
  "#C8A97E", "#D97706", "#EF4444", "#EC4899", "#A855F7",
  "#3B82F6", "#10B981", "#0EA5E9", "#64748B",
];

const EMPTY = { name: "", role: "makeup", color: COLOR_PALETTE[0], email: "", phone: "", specialty: "" };

// Migrate legacy role values into the new editable set so the dropdown
// shows the right value when opening "Modifica" on an old record.
const normalizeRole = (r) => {
  if (r === "makeup_artist") return "makeup";
  if (r === "hairstylist") return "hair";
  if (r === "assistant") return "assistente";
  return r || "makeup";
};

export default function Team() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [busy, setBusy] = useState(false);

  // Delete confirmation state
  const [confirmTarget, setConfirmTarget] = useState(null); // { id, name, total }
  const [confirmBusy, setConfirmBusy] = useState(false);

  const load = async () => { try { const { data } = await api.get("/team"); setItems(data); } catch {} };
  useEffect(() => { load(); }, []);

  const openNew = () => {
    setEditingId(null);
    setForm(EMPTY);
    setOpen(true);
  };

  const openEdit = (m) => {
    setEditingId(m.id);
    setForm({
      name: m.name || "",
      role: normalizeRole(m.role),
      color: m.color || COLOR_PALETTE[0],
      email: m.email || "",
      phone: m.phone || "",
      specialty: m.specialty || "",
    });
    setOpen(true);
  };

  const save = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) { toast.error("Inserisci il nome del membro"); return; }
    setBusy(true);
    try {
      if (editingId) {
        await api.put(`/team/${editingId}`, form);
        toast.success("Membro aggiornato");
      } else {
        await api.post("/team", form);
        toast.success("Membro aggiunto");
      }
      setOpen(false); setForm(EMPTY); setEditingId(null);
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally { setBusy(false); }
  };

  const askDelete = async (m) => {
    try {
      const { data } = await api.get(`/team/${m.id}/usage`);
      setConfirmTarget({ id: m.id, name: m.name, total: data.total || 0 });
    } catch {
      setConfirmTarget({ id: m.id, name: m.name, total: 0 });
    }
  };

  const confirmDelete = async () => {
    if (!confirmTarget) return;
    setConfirmBusy(true);
    try {
      const cascade = confirmTarget.total > 0;
      await api.delete(`/team/${confirmTarget.id}`, { params: cascade ? { cascade: true } : {} });
      toast.success(cascade
        ? `Membro rimosso e ${confirmTarget.total} assegnazione/i cancellata/e`
        : "Membro rimosso");
      setConfirmTarget(null);
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally { setConfirmBusy(false); }
  };

  const roleLabel = (v) => ROLES.find((r) => r.v === v)?.label || ROLE_LABEL_LEGACY[v] || v;
  const roleIcon = (v) => ROLES.find((r) => r.v === v)?.icon || (v === "assistant" ? "🤝" : "");

  return (
    <div className="space-y-6" data-testid="team-page">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <span className="label-uppercase">Collaboratori</span>
          <h1 className="font-serif-heading text-4xl sm:text-5xl tracking-tight mt-2">Team</h1>
          <p className="text-sm text-neutral-600 mt-2">Assegna hairstylist, makeup artist e assistenti ai tuoi eventi.</p>
        </div>
        <Button className="rounded-sm" onClick={openNew} data-testid="team-new-button">
          <Plus className="w-4 h-4 mr-2" strokeWidth={1.5} />Aggiungi membro
        </Button>
      </div>

      {/* Shared dialog: new + edit */}
      <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) { setEditingId(null); setForm(EMPTY); } }}>
        <DialogContent className="rounded-sm max-w-lg" data-testid="team-form-dialog">
          <DialogHeader>
            <DialogTitle className="font-serif-heading text-2xl">
              {editingId ? "Modifica membro" : "Nuovo membro"}
            </DialogTitle>
            <DialogDescription>
              {editingId
                ? "Aggiorna i dati del collaboratore. Le assegnazioni esistenti sui clienti restano invariate."
                : "Aggiungi un collaboratore al tuo team."}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={save} className="space-y-4">
            <div>
              <Label className="label-uppercase">Nome *</Label>
              <Input
                required
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                className="mt-1 rounded-sm"
                data-testid="team-name-input"
              />
            </div>
            <div>
              <Label className="label-uppercase">Ruolo</Label>
              <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                <SelectTrigger className="mt-1 rounded-sm" data-testid="team-role-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {ROLES.map((r) => (
                    <SelectItem key={r.v} value={r.v}>
                      <span className="mr-2">{r.icon}</span>{r.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="label-uppercase">Colore (calendario)</Label>
              <div className="mt-2 flex flex-wrap gap-2" data-testid="team-color-palette">
                {COLOR_PALETTE.map((c) => (
                  <button
                    key={c}
                    type="button"
                    onClick={() => setForm({ ...form, color: c })}
                    className={`w-8 h-8 rounded-full border-2 transition-all ${form.color === c ? "border-foreground scale-110" : "border-transparent hover:border-neutral-300"}`}
                    style={{ backgroundColor: c }}
                    aria-label={`Colore ${c}`}
                    data-testid={`team-color-${c.replace('#', '')}`}
                  />
                ))}
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="label-uppercase">Email</Label>
                <Input
                  type="email"
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  className="mt-1 rounded-sm"
                  data-testid="team-email-input"
                />
              </div>
              <div>
                <Label className="label-uppercase">Telefono</Label>
                <Input
                  value={form.phone}
                  onChange={(e) => setForm({ ...form, phone: e.target.value })}
                  className="mt-1 rounded-sm"
                  data-testid="team-phone-input"
                />
              </div>
            </div>
            <div>
              <Label className="label-uppercase">Specialità</Label>
              <Input
                value={form.specialty}
                onChange={(e) => setForm({ ...form, specialty: e.target.value })}
                className="mt-1 rounded-sm"
                placeholder="Es. acconciature eleganti, smokey eyes..."
                data-testid="team-specialty-input"
              />
            </div>
            <DialogFooter>
              <Button type="submit" disabled={busy} className="rounded-sm" data-testid="team-save-button">
                {busy ? "Salvataggio…" : (editingId ? "Salva modifiche" : "Salva")}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {items.length === 0 ? (
        <div className="bg-white border border-border rounded-sm p-16 text-center">
          <UserCog className="w-10 h-10 mx-auto text-primary" strokeWidth={1.5} />
          <h3 className="font-serif-heading text-2xl mt-4">Team vuoto</h3>
          <p className="text-neutral-600 mt-2">Aggiungi i tuoi collaboratori per assegnarli agli eventi.</p>
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {items.map((m) => (
            <div key={m.id} className="bg-white border border-border rounded-sm p-6" data-testid={`team-card-${m.id}`}>
              <div className="flex items-start justify-between gap-3">
                <div className="flex items-start gap-3 min-w-0">
                  {m.color && (
                    <span
                      className="w-3 h-3 rounded-full mt-2 shrink-0 border border-black/10"
                      style={{ backgroundColor: m.color }}
                      data-testid={`team-color-dot-${m.id}`}
                    />
                  )}
                  <div className="min-w-0">
                    <div className="label-uppercase text-primary">
                      {roleIcon(m.role) && <span className="mr-1">{roleIcon(m.role)}</span>}
                      {roleLabel(m.role)}
                    </div>
                    <h3 className="font-serif-heading text-2xl mt-2 truncate">{m.name}</h3>
                  </div>
                </div>
                <div className="flex items-center gap-1 shrink-0">
                  <button
                    type="button"
                    onClick={() => openEdit(m)}
                    className="w-8 h-8 inline-flex items-center justify-center rounded-sm text-neutral-400 hover:text-primary hover:bg-secondary/60 transition-colors"
                    data-testid={`team-edit-${m.id}`}
                    aria-label="Modifica membro"
                  >
                    <Pencil className="w-4 h-4" strokeWidth={1.5} />
                  </button>
                  <button
                    type="button"
                    onClick={() => askDelete(m)}
                    className="w-8 h-8 inline-flex items-center justify-center rounded-sm text-neutral-400 hover:text-destructive hover:bg-destructive/5 transition-colors"
                    data-testid={`team-delete-${m.id}`}
                    aria-label="Elimina membro"
                  >
                    <Trash2 className="w-4 h-4" strokeWidth={1.5} />
                  </button>
                </div>
              </div>
              {m.specialty && <p className="text-sm text-neutral-600 mt-3 italic">{m.specialty}</p>}
              <div className="mt-4 space-y-1 text-sm text-neutral-600">
                {m.email && <div className="flex items-center gap-2 truncate"><Mail className="w-3.5 h-3.5" strokeWidth={1.5} />{m.email}</div>}
                {m.phone && <div className="flex items-center gap-2"><Phone className="w-3.5 h-3.5" strokeWidth={1.5} />{m.phone}</div>}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Delete confirmation with cascade option when assigned */}
      <AlertDialog open={!!confirmTarget} onOpenChange={(v) => { if (!v) setConfirmTarget(null); }}>
        <AlertDialogContent className="rounded-sm" data-testid="team-delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              {confirmTarget?.total > 0 && <AlertTriangle className="w-5 h-5 text-amber-600" strokeWidth={1.5} />}
              Eliminare {confirmTarget?.name || "il membro"}?
            </AlertDialogTitle>
            <AlertDialogDescription>
              {confirmTarget?.total > 0 ? (
                <>
                  Questo operatore è assegnato a <strong>{confirmTarget.total}</strong> cliente/i.
                  Confermando, le assegnazioni verranno rimosse automaticamente. L'operazione è irreversibile.
                </>
              ) : (
                "Il membro non è assegnato a nessun cliente. L'operazione è irreversibile."
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-sm" data-testid="team-delete-cancel">Annulla</AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmDelete}
              disabled={confirmBusy}
              className="rounded-sm bg-destructive hover:bg-destructive/90"
              data-testid="team-delete-confirm"
            >
              {confirmBusy
                ? "Rimuovo…"
                : (confirmTarget?.total > 0 ? "Rimuovi e cancella assegnazioni" : "Elimina")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
